import React, { forwardRef, useImperativeHandle, useEffect } from 'react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import TextAlign from '@tiptap/extension-text-align';
import Underline from '@tiptap/extension-underline';
import Highlight from '@tiptap/extension-highlight';
import TextStyle from '@tiptap/extension-text-style';
import Color from '@tiptap/extension-color';
import Placeholder from '@tiptap/extension-placeholder';
import Image from '@tiptap/extension-image';
import ResizableImage from './ResizableImage';
import {
  Bold,
  Italic,
  Underline as UnderlineIcon,
  List,
  ListOrdered,
  AlignLeft,
  AlignCenter,
  AlignRight,
  Link as LinkIcon,
  Highlighter,
  Palette,
  Undo,
  Redo,
} from 'lucide-react';

interface WYSIWYGEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  error?: string;
  companyLogoUrl?: string;
}

export interface WYSIWYGEditorRef {
  insertHTML: (html: string) => void;
  insertImage: (src: string, alt?: string) => void;
  insertHeading: (text: string, level?: number) => void;
  insertParagraph: (text: string) => void;
  insertCompanyLogo: () => void;
  getContent: () => string;
}

const WYSIWYGEditor = forwardRef<WYSIWYGEditorRef, WYSIWYGEditorProps>((props, ref) => {
  const {
    value,
    onChange,
    placeholder,
    disabled = false,
    error,
    companyLogoUrl,
  } = props;

  const editor = useEditor({
    extensions: [
      StarterKit,
      Link.configure({
        HTMLAttributes: {
          class: 'text-accent-color underline',
        },
      }),
      TextAlign.configure({
        types: ['heading', 'paragraph'],
      }),
      Underline,
      Highlight,
      TextStyle,
      Color.configure({
        types: ['textStyle'],
      }),
      Placeholder.configure({
        placeholder,
      }),
      Image.configure({
        HTMLAttributes: {
          class: 'max-w-full h-auto cursor-pointer',
        },
        inline: false,
        allowBase64: true,
      }),
    ],
    content: value,
    editable: !disabled,
    onUpdate: ({ editor }) => {
      onChange(editor.getHTML());
    },
  });


  useEffect(() => {
    if (!editor) return;

    const incoming = value || '';
    const current = editor.getHTML();
    const isEmpty = (html: string) => !html || html === '<p></p>' || html === '<p><br></p>';

    if (isEmpty(current) && isEmpty(incoming)) return;
    if (current !== incoming) {
      editor.commands.setContent(incoming, false);
    }
  }, [editor, value]);

  useImperativeHandle(ref, () => ({
    insertHTML: (html: string) => {
      if (editor) {
        // Insert the HTML directly - TipTap should parse it with the Image extension
        editor.chain().focus().insertContent(html).run();
      }
    },
    insertImage: (src: string, alt: string = '') => {
      if (editor) {
        editor.chain().focus().setImage({ src, alt }).run();
      }
    },
    insertHeading: (text: string, level: number = 1) => {
      if (editor) {
        editor.chain().focus().setHeading({ level }).insertContent(text).run();
      }
    },
    insertParagraph: (text: string) => {
      if (editor) {
        editor.chain().focus().setParagraph().insertContent(text).run();
      }
    },
    insertCompanyLogo: () => {
      if (editor) {
        const logoUrl = companyLogoUrl || '{{CompanyLogo}}';
        
        // Insert a placeholder div that we'll replace with ResizableImage
        editor.chain().focus()
          .setParagraph()
          .insertContent(`
            <div class="resizable-image-placeholder" data-logo-url="${logoUrl}" style="text-align: center; margin: 20px 0; border: 2px dashed #ccc; padding: 20px;">
              <p>Company Logo (Click to resize)</p>
            </div>
          `)
          .run();
      }
    },
    getContent: () => {
      if (editor) {
        return editor.getHTML();
      }
      return '';
    },
  }), [editor, companyLogoUrl]);

  if (!editor) {
    return <div>Loading editor...</div>;
  }

  return (
    <div className="relative">
      {/* Toolbar */}
      <div className="border border-gray-300 dark:border-gray-600 rounded-t-md bg-gray-50 dark:bg-gray-700 p-2 flex flex-wrap gap-1">
        <button
          onClick={() => editor.chain().focus().toggleBold().run()}
          disabled={disabled || !editor.can().chain().focus().toggleBold().run()}
          className={`p-2 rounded hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
        >
          <Bold className="h-4 w-4" />
        </button>

        <button
          onClick={() => editor.chain().focus().toggleItalic().run()}
          disabled={disabled || !editor.can().chain().focus().toggleItalic().run()}
          className={`p-2 rounded hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
        >
          <Italic className="h-4 w-4" />
        </button>

        <button
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          disabled={disabled || !editor.can().chain().focus().toggleUnderline().run()}
          className={`p-2 rounded hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
        >
          <UnderlineIcon className="h-4 w-4" />
        </button>

        <div className="w-px h-6 bg-gray-300 dark:bg-gray-600 mx-1" />

        <button
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          disabled={disabled || !editor.can().chain().focus().toggleBulletList().run()}
          className={`p-2 rounded hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
        >
          <List className="h-4 w-4" />
        </button>

        <button
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          disabled={disabled || !editor.can().chain().focus().toggleOrderedList().run()}
          className={`p-2 rounded hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
        >
          <ListOrdered className="h-4 w-4" />
        </button>

        <div className="w-px h-6 bg-gray-300 dark:bg-gray-600 mx-1" />

        <button
          onClick={() => editor.chain().focus().setTextAlign('left').run()}
          disabled={disabled || !editor.can().chain().focus().setTextAlign('left').run()}
          className={`p-2 rounded hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
        >
          <AlignLeft className="h-4 w-4" />
        </button>

        <button
          onClick={() => editor.chain().focus().setTextAlign('center').run()}
          disabled={disabled || !editor.can().chain().focus().setTextAlign('center').run()}
          className={`p-2 rounded hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
        >
          <AlignCenter className="h-4 w-4" />
        </button>

        <button
          onClick={() => editor.chain().focus().setTextAlign('right').run()}
          disabled={disabled || !editor.can().chain().focus().setTextAlign('right').run()}
          className={`p-2 rounded hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
        >
          <AlignRight className="h-4 w-4" />
        </button>

        <div className="w-px h-6 bg-gray-300 dark:bg-gray-600 mx-1" />

        <button
          onClick={() => editor.chain().focus().toggleHighlight().run()}
          disabled={disabled || !editor.can().chain().focus().toggleHighlight().run()}
          className={`p-2 rounded hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
        >
          <Highlighter className="h-4 w-4" />
        </button>

        <button
          onClick={() => editor.chain().focus().setColor('#3b82f6').run()}
          disabled={disabled || !editor.can().chain().focus().setColor('#3b82f6').run()}
          className={`p-2 rounded hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
        >
          <Palette className="h-4 w-4" />
        </button>

        <div className="w-px h-6 bg-gray-300 dark:bg-gray-600 mx-1" />

        <button
          onClick={() => editor.chain().focus().undo().run()}
          disabled={disabled || !editor.can().chain().focus().undo().run()}
          className={`p-2 rounded hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
        >
          <Undo className="h-4 w-4" />
        </button>

        <button
          onClick={() => editor.chain().focus().redo().run()}
          disabled={disabled || !editor.can().chain().focus().redo().run()}
          className={`p-2 rounded hover:bg-gray-200 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-300 ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
        >
          <Redo className="h-4 w-4" />
        </button>
      </div>

      {/* Editor content */}
      <div
        className={`border border-t-0 border-gray-300 dark:border-gray-600 rounded-b-md bg-white dark:bg-gray-800 flex-1 ${
          error ? 'border-red-300 dark:border-red-600' : ''
        }`}
        style={{ minHeight: '300px', maxHeight: '600px', overflowY: 'auto' }}
        onClick={() => editor?.chain().focus().run()}
      >
        <EditorContent
          editor={editor}
          className="min-h-[300px] p-3 outline-none focus:outline-none focus:ring-0 cursor-text"
        />
      </div>

      {/* Remove default ProseMirror outlines that appear as black borders */}
      <style>{`
        .ProseMirror:focus { outline: none; }
        .ProseMirror-selectednode { outline: none; }
      `}</style>

      {/* Error message */}
      {error && (
        <p className="mt-1 text-sm text-red-600 dark:text-red-400">{error}</p>
      )}
    </div>
  );
});

WYSIWYGEditor.displayName = 'WYSIWYGEditor';

export default WYSIWYGEditor;
